import React, { useEffect, useState } from "react";
import { useAuth, WS_STATUS } from "@/contexts/AuthContext";
import { WS_COPY, pick } from "@/lib/wsCopy";
import useIsMobile from "@/lib/useIsMobile";
import { RefreshCw, WifiOff, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * THE global banner — the sole owner of the "something's wrong" message.
 *
 * §4: the top bar name, the usage badge, gated buttons and page bodies say
 * NOTHING about the error. They go neutral-blank. Never more than one banner,
 * one button, one voice. If you are adding a second error surface for workspace
 * failure, you are adding a bug.
 *
 * Register is amber (#F57C00 family), NEVER red. Red is for something the user
 * did or lost; this is neither.
 */

/**
 * The countdown IS the proof of life — §2 S3b. Under reduced motion it is the
 * only motion left on screen, and it is semantic, not decorative.
 * The numeral is aria-hidden so the live region announces once per state
 * change, not once per tick (§10).
 */
function Countdown({ until }) {
  const [secs, setSecs] = useState(() => Math.max(0, Math.ceil((until - Date.now()) / 1000)));
  useEffect(() => {
    setSecs(Math.max(0, Math.ceil((until - Date.now()) / 1000)));
    const id = setInterval(() => {
      setSecs(Math.max(0, Math.ceil((until - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [until]);
  // The ticking line is hidden from screen readers wholesale — announcing
  // "Next try in 4s… 3s… 2s…" into a polite live region is a nuisance, not
  // information. SR users get one stable sentence for the whole S3b state.
  return (
    <>
      <span aria-hidden="true" data-testid="ws-banner-countdown">
        Next try in {secs}s
      </span>
      <span className="sr-only">Waiting to try again.</span>
    </>
  );
}

export default function WorkspaceBanner() {
  const {
    workspaceStatus,
    wsAttempt,
    wsRequestOpen,
    wsNextRetryAt,
    wsRefCode,
    wsOffline,
    wsLaddersExhausted,
    wsHealing,
    watchStatus,
    wsAuthFailed,
    retryWorkspace,
  } = useAuth();
  const isMobile = useIsMobile();

  const retrying = workspaceStatus === WS_STATUS.RETRYING;
  const unreachable = workspaceStatus === WS_STATUS.UNREACHABLE;

  // An auth failure is ProtectedRoute's job — a redirect to login, not an amber
  // banner on top of it. Stay out of the way.
  if (wsAuthFailed) return null;
  if (!retrying && !unreachable && !wsHealing) return null;

  // ── S5 Healing ─────────────────────────────────────────────────────────
  if (wsHealing) {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="ws-banner-healed"
        data-ws-state="healed"
        className="sticky top-16 z-[9] w-full border-b border-green-200 bg-green-50 text-green-900 px-4 md:px-8 py-3 transition-colors duration-300 motion-reduce:transition-none"
      >
        <div className="max-w-[1400px] mx-auto flex items-center gap-2.5 text-sm">
          <CheckCircle2 className="w-4 h-4 shrink-0 text-[#2E7D32]" strokeWidth={2.2} />
          <span className="font-medium">{pick(WS_COPY.healed, isMobile)}</span>
        </div>
      </div>
    );
  }

  const offline = wsOffline;
  const title = offline
    ? pick(WS_COPY.offline.title, isMobile)
    : unreachable
    ? pick(WS_COPY.down.title, isMobile)
    : pick(WS_COPY.retry.title, isMobile);

  const cta = offline ? pick(WS_COPY.offline.cta, isMobile) : pick(WS_COPY.down.cta, isMobile);

  // §5: one primary action — retry. The secondary "reload the page" appears
  // ONLY after two exhausted ladders (~90s), as the honest last resort.
  const showLastResort = unreachable && wsLaddersExhausted >= 2;

  const body = [];
  if (offline) {
    // ws.offline.body promises "We'll reconnect on our own the moment you're
    // back." The `online` listener in AuthContext is what makes that true.
    const b = pick(WS_COPY.offline.body, isMobile);
    (Array.isArray(b) ? b : [b]).forEach((l) => body.push(l));
  } else if (unreachable) {
    const b = pick(WS_COPY.down.body, isMobile);
    (Array.isArray(b) ? b : [b]).forEach((l) => body.push(l));
  }

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="ws-banner"
      data-ws-state={offline ? "offline" : unreachable ? "unreachable" : "retrying"}
      className={[
        "sticky top-16 z-[9] w-full border-b px-4 md:px-8 py-3",
        // S4 deepens the colour (300ms) rather than switching to red — it is
        // the same sentence continuing, not a second, worse alarm.
        unreachable
          ? "border-amber-300 bg-amber-100 text-amber-950"
          : "border-amber-200 bg-amber-50 text-amber-900",
        "transition-colors duration-300 motion-reduce:transition-none",
        // Rise-in: 200ms opacity + 4px. Killed entirely under reduced motion.
        "motion-safe:animate-[wsBannerIn_200ms_ease-out]",
      ].join(" ")}
    >
      <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex items-start gap-2.5 flex-1 min-w-0">
          {offline ? (
            <WifiOff className="w-4 h-4 shrink-0 mt-0.5 text-[#F57C00]" strokeWidth={2.2} />
          ) : wsRequestOpen && retrying ? (
            // S3a: spinner ONLY while a request is genuinely open.
            <RefreshCw
              className="w-4 h-4 shrink-0 mt-0.5 text-[#F57C00] motion-safe:animate-spin motion-reduce:animate-none"
              strokeWidth={2.2}
              aria-hidden="true"
            />
          ) : (
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-[#F57C00]" strokeWidth={2.2} />
          )}

          <div className="min-w-0 text-sm leading-relaxed">
            <div className="font-semibold" data-testid="ws-banner-title">
              {title}
            </div>

            {/* S3a / S3b — mutually exclusive, and each is only true in its
                own state. `ws.retry.attempt` says "now": it renders ONLY while
                wsRequestOpen. Otherwise the countdown carries the beat. */}
            {retrying && !offline && (
              <div className="mt-0.5 text-amber-900/90" data-testid="ws-banner-line2">
                {wsRequestOpen ? (
                  <span data-testid="ws-banner-attempt">
                    {pick(WS_COPY.retry.attempt, isMobile, wsAttempt)}
                  </span>
                ) : wsNextRetryAt ? (
                  <Countdown until={wsNextRetryAt} />
                ) : null}
              </div>
            )}

            {body.map((line, i) => (
              <div key={i} className="mt-0.5 text-amber-900/90">
                {line}
              </div>
            ))}

            {/* ws.down.watching may ONLY render while a poll genuinely runs.
                When it caps out we RETRACT it rather than hiding it — the
                user's last information must not remain "they're watching for
                me". Honesty condition 1. */}
            {unreachable && (
              <div className="mt-1 text-xs text-amber-900/75" data-testid="ws-banner-watching">
                {watchStatus === "polling"
                  ? pick(WS_COPY.down.watching, isMobile)
                  : pick(WS_COPY.down.watchingPaused, isMobile)}
              </div>
            )}

            {showLastResort && (
              <div className="mt-1 text-xs text-amber-900/75" data-testid="ws-banner-lastresort">
                {pick(WS_COPY.down.lastresort, isMobile)}
              </div>
            )}

            {/* Unlabelled by design: a bare grey monospace string reads as an
                incidental serial number. Captioning it "Error" adds a red word
                to an amber banner. The server's `detail` went to console.error
                and stops there. */}
            {(unreachable || offline) && wsRefCode && (
              <div
                className="mt-1.5 font-mono text-[11px] text-lm-text3"
                data-testid="ws-banner-ref"
              >
                {wsRefCode}
              </div>
            )}
          </div>
        </div>

        {/* §10: ONE button on screen, ever. Inline right ≥768px; full-width
            beneath the text on mobile, 44px min tap target. */}
        {(unreachable || offline) && (
          <div className="shrink-0 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={retryWorkspace}
              data-testid="ws-banner-retry"
              className="inline-flex items-center justify-center gap-1.5 rounded-md font-medium text-sm border border-amber-400 bg-white text-amber-900 hover:bg-amber-50 transition-colors px-4 min-h-[44px] w-full md:w-auto"
            >
              <RefreshCw className="w-3.5 h-3.5" strokeWidth={2.2} />
              {cta}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
